import {
  FINANCIAL_STATEMENT_TEMPLATE_EXPENSE_COMPONENTS,
  FINANCIAL_STATEMENT_FORM_TEMPLATES,
  INITIAL_BANK_OPTIONS_COUNT,
  MAX_DECIMAL_FRACTION_DIGITS,
  MONTH_OPTIONS,
  PRIORITY_CURRENCY_CODES,
  QUARTER_MONTHS,
  REQUIRED_EXPENSE_REPORT_LINE_CODES,
  REQUIRED_LIABILITY_REPORT_LINE_CODES,
  REQUIRED_RECEIVABLE_TEMPLATE_CODES,
  REPORT_PERIOD_OPTIONS,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/constants.ts';
import type { TAutocompleteOption } from '~/components/AutocompleteField/types.ts';
import type { FieldPath } from 'react-hook-form';
import type {
  TDocumentGenerationBank,
  TDocumentGenerationCurrency,
} from '~/store/covenants/types.ts';
import type {
  TBankAverageBalanceRow,
  TBankAverageBalanceCurrencyTotals,
  TBankIncomeRow,
  TBankIncomeCurrencyTotals,
  TCreditOperationCurrencyTotals,
  TCurrencyExchange,
  TCurrencyExchangeCurrencyTotals,
  TCreditOperation,
  TFinancialAssistance,
  TFinancialAssistanceCurrencyTotals,
  TFinancialConditionFormValues,
  TLoanOrBond,
  TLiability,
  TIncomeBreakdown,
  TFinancialInvestment,
  TReceivable,
  TBudgetDebt,
  TExpense,
  TCourtCase,
  TOffBalanceLiability,
  TQuarter,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import { FinancialStatementForm } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import type { FilterOptionsState } from '@mui/material';
import { muiFilter } from '~/components/FormComponents/utils.ts';
import { formatDateForDisplay } from '~/helpers/utils.ts';

export const getDocumentGenerationBankOptions = (
  banks: TDocumentGenerationBank[]
): TAutocompleteOption[] =>
  banks
    .map((bank) => {
      const name = bank.fullName ?? bank.name ?? bank.shortname ?? bank.sname;

      if (!name) {
        return null;
      }

      const bankCode = bank.idNbu === null || bank.idNbu === undefined ? null : String(bank.idNbu);

      return {
        value: String(bank.id),
        label: bankCode ? `${name} (МФО ${bankCode})` : name,
      };
    })
    .filter((option): option is TAutocompleteOption => !!option);

export const getDocumentGenerationShortBankOptions = (
  banks: TDocumentGenerationBank[]
): TAutocompleteOption[] =>
  banks
    .map((bank) => {
      const name = bank.shortname ?? bank.sname ?? bank.name ?? bank.fullName;

      return name
        ? {
            value: String(bank.id),
            label: name,
          }
        : null;
    })
    .filter((option): option is TAutocompleteOption => !!option);

export const getDocumentGenerationCurrencyOptions = (
  currencies: TDocumentGenerationCurrency[]
): TAutocompleteOption[] =>
  currencies
    .map((currency) => {
      const symbolCode = currency.symbolCode?.trim();

      return symbolCode ? { value: String(currency.id), label: symbolCode } : null;
    })
    .filter((option): option is TAutocompleteOption => !!option)
    .sort((first, second) => {
      const firstPriority = PRIORITY_CURRENCY_CODES.indexOf(
        first.label as (typeof PRIORITY_CURRENCY_CODES)[number]
      );
      const secondPriority = PRIORITY_CURRENCY_CODES.indexOf(
        second.label as (typeof PRIORITY_CURRENCY_CODES)[number]
      );

      if (firstPriority !== -1 || secondPriority !== -1) {
        if (firstPriority === -1) return 1;
        if (secondPriority === -1) return -1;

        return firstPriority - secondPriority;
      }

      return first.label.localeCompare(second.label);
    });

export const filterBankOptions = (
  options: TAutocompleteOption[],
  state: FilterOptionsState<TAutocompleteOption>
) => {
  const filteredOptions = muiFilter(options, state);

  return state.inputValue.trim()
    ? filteredOptions
    : filteredOptions.slice(0, INITIAL_BANK_OPTIONS_COUNT);
};

export const createBankIncomeRow = (): TBankIncomeRow => ({
  bankId: null,
  currency: null,
  total: '',
  salesRevenue: '',
});

export const createBankAverageBalanceRow = (): TBankAverageBalanceRow => ({
  bankId: null,
  currency: null,
  firstMonthAmount: '',
  secondMonthAmount: '',
  thirdMonthAmount: '',
});

export const createCreditOperation = (): TCreditOperation => ({
  lenderName: null,
  productType: null,
  currency: null,
  limit: '',
  outstandingDebt: '',
  interestRate: '',
  issueDate: null,
  maturityDate: null,
  overdueDebt: '',
});

export const createFinancialAssistance = (): TFinancialAssistance => ({
  counterpartyName: '',
  currency: null,
  outstandingDebt: '',
  issueOrReceiptDate: null,
  maturityDate: null,
  balanceLine: '',
  overdueDebt: '',
});

export const createLoanOrBond = (): TLoanOrBond => ({
  lenderName: null,
  lenderCode: '',
  receivedDate: null,
  maturityDate: null,
  currency: null,
  currentAmount: '',
  balanceLine: '',
  overdueDebt: '',
});

export const createOffBalanceLiability = (): TOffBalanceLiability => ({
  debtor: '',
  creditor: null,
  obligationType: '',
  currency: null,
  limitAmount: '',
  actualAmount: '',
  interestRate: '',
  openDate: null,
  endDate: null,
});

export const createCurrencyExchange = (): TCurrencyExchange => ({
  bank: null,
  soldCurrency: null,
  soldAmount: '',
  boughtCurrency: null,
  boughtAmount: '',
});

export const createLiability = (): TLiability => ({
  creditor: null,
  liabilityType: '',
  amount: '',
  purpose: '',
  originDate: null,
  repaymentDate: null,
  reportLine: '',
});

export const createIncomeBreakdown = (): TIncomeBreakdown => ({
  counterpartyName: '',
  counterpartyEdrpou: '',
  reportLine: '',
  incomeType: '',
  amount: '',
  sharePercent: '',
});

export const createFinancialInvestment = (): TFinancialInvestment => ({
  templateCode: undefined,
  name: '',
  edrpou: '',
  sharePercent: '',
  amount: '',
  startDate: null,
});

export const createReceivable = (): TReceivable => ({
  templateCode: undefined,
  counterpartyName: '',
  edrpou: '',
  subject: '',
  totalDebt: '',
  overdueAmount: '',
  originDate: null,
  repaymentDate: null,
});

export const createBudgetDebt = (): TBudgetDebt => ({
  debtType: '',
  amount: '',
});

export const createExpense = (): TExpense => ({
  reportLine: '',
  component: '',
  amount: '',
  sharePercent: '',
});

export const createRowsWithReportLines = <T extends { reportLine: string }>(
  reportLineCodes: readonly string[],
  createRow: () => T
): T[] =>
  reportLineCodes.map((reportLine) => ({
    ...createRow(),
    reportLine: `р.${reportLine}`,
  }));

export const createRowsWithTemplateCodes = <T extends { templateCode?: string }>(
  templateCodes: readonly string[],
  createRow: () => T
): T[] =>
  templateCodes.map((templateCode) => ({
    ...createRow(),
    templateCode,
  }));

export const createExpenseTemplateRows = (
  templateRows: readonly { reportLine?: string; component?: string }[]
): TExpense[] =>
  templateRows.map((row) => ({
    ...createExpense(),
    reportLine: row.reportLine ? `р.${row.reportLine}` : '',
    component: row.component ?? '',
  }));

const hasFinancialStatementRowContent = (row: Record<string, unknown>) => {
  const filledKeys = Object.entries(row)
    .filter(
      ([key, value]) =>
        key !== 'reportLine' &&
        key !== 'templateCode' &&
        value !== null &&
        value !== undefined &&
        (!(typeof value === 'string') || Boolean(value.trim())) &&
        (!(value instanceof Date) || !Number.isNaN(value.getTime()))
    )
    .map(([key]) => key);

  return (
    !(
      filledKeys.length === 1 &&
      filledKeys[0] === 'component' &&
      FINANCIAL_STATEMENT_TEMPLATE_EXPENSE_COMPONENTS.includes(
        row.component as (typeof FINANCIAL_STATEMENT_TEMPLATE_EXPENSE_COMPONENTS)[number]
      )
    ) && Boolean(filledKeys.length)
  );
};

const mergeFinancialStatementRows = <T extends { reportLine: string }>(
  loadedRows: T[],
  localRows: T[]
): T[] => {
  const remainingRows = [...loadedRows];

  localRows
    .filter((row) => hasFinancialStatementRowContent(row as Record<string, unknown>))
    .forEach((localRow) => {
      const matchingRowIndex = localRow.reportLine
        ? remainingRows.findIndex((row) => row.reportLine === localRow.reportLine)
        : -1;

      if (matchingRowIndex !== -1) {
        remainingRows[matchingRowIndex] = localRow;
        return;
      }

      remainingRows.push(localRow);
    });

  return remainingRows;
};

const mergeTemplateFinancialStatementRows = <T extends { templateCode?: string }>(
  loadedRows: T[],
  localRows: T[]
): T[] => {
  const remainingRows = [...loadedRows];

  localRows
    .filter((row) => hasFinancialStatementRowContent(row as Record<string, unknown>))
    .forEach((localRow) => {
      const matchingRowIndex = localRow.templateCode
        ? remainingRows.findIndex((row) => row.templateCode === localRow.templateCode)
        : -1;

      if (matchingRowIndex !== -1) {
        remainingRows[matchingRowIndex] = localRow;
        return;
      }

      remainingRows.push(localRow);
    });

  return remainingRows;
};

type TFinancialConditionFieldPath = FieldPath<TFinancialConditionFormValues>;

const getRequiredFinancialStatementFieldNames = (
  values: TFinancialConditionFormValues
): TFinancialConditionFieldPath[] => {
  const financialStatementForm = values.financialStatementForm?.value as FinancialStatementForm;

  if (!financialStatementForm) {
    return [];
  }

  const getRowFieldNames = <T extends { reportLine?: string; templateCode?: string }>(
    rows: T[],
    rowKey: 'reportLine' | 'templateCode',
    code: string,
    fieldPath: string
  ): TFinancialConditionFieldPath[] =>
    rows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => row[rowKey]?.replace(/\D/g, '') === code)
      .map(({ index }) => `${fieldPath}.${index}` as TFinancialConditionFieldPath);

  return [
    ...REQUIRED_LIABILITY_REPORT_LINE_CODES.flatMap((code) =>
      getRowFieldNames(values.liabilitiesAnnual, 'reportLine', code, 'liabilitiesAnnual')
    ),
    ...REQUIRED_RECEIVABLE_TEMPLATE_CODES.flatMap((code) =>
      getRowFieldNames(values.receivables, 'templateCode', code, 'receivables')
    ),
    ...(financialStatementForm === FinancialStatementForm.SmallOrSimplified
      ? REQUIRED_EXPENSE_REPORT_LINE_CODES.flatMap((code) =>
          getRowFieldNames(
            values.expensesQuarterlySmall,
            'reportLine',
            code,
            'expensesQuarterlySmall'
          )
        )
      : []),
  ];
};

export const getMissingRequiredFinancialStatementFieldNames = (
  values: TFinancialConditionFormValues
): TFinancialConditionFieldPath[] => {
  const allRequiredFieldNames = getRequiredFinancialStatementFieldNames(values);

  const isRowFilled = (fieldPath: string) => {
    const [collectionName, indexValue] = fieldPath.split('.');
    const collection = values[collectionName as keyof TFinancialConditionFormValues];
    const row = Array.isArray(collection) ? collection[Number(indexValue)] : undefined;

    return row && hasFinancialStatementRowContent(row as Record<string, unknown>);
  };

  return allRequiredFieldNames.filter((fieldPath) => !isRowFilled(fieldPath));
};

export const getRequiredFinancialStatementErrorFieldNames = (
  values: TFinancialConditionFormValues
): TFinancialConditionFieldPath[] =>
  getRequiredFinancialStatementFieldNames(values).map((fieldPath) => {
    const [collectionName, indexValue] = fieldPath.split('.');
    const errorField =
      collectionName === 'financialInvestments'
        ? 'name'
        : collectionName === 'receivables'
          ? 'counterpartyName'
          : collectionName === 'expensesQuarterlySmall'
            ? 'amount'
            : 'creditor';

    return `${collectionName}.${indexValue}.${errorField}` as TFinancialConditionFieldPath;
  });

export const getMissingFinancialConditionRequiredFieldNames = (
  values: TFinancialConditionFormValues
): TFinancialConditionFieldPath[] => {
  const missingFieldNames: TFinancialConditionFieldPath[] = [];

  if (!values.reportDate) {
    missingFieldNames.push('reportDate');
  }

  if (values.borrowerType?.value === 'legalEntity' && !values.financialStatementForm) {
    missingFieldNames.push('financialStatementForm');
  }

  const missingRowPaths = new Set(getMissingRequiredFinancialStatementFieldNames(values));

  getRequiredFinancialStatementErrorFieldNames(values).forEach((fieldName) => {
    const rowPath = fieldName.split('.').slice(0, 2).join('.');

    if (missingRowPaths.has(rowPath as TFinancialConditionFieldPath)) {
      missingFieldNames.push(fieldName);
    }
  });

  return missingFieldNames;
};

export const mergeFinancialStatementFormValues = (
  loadedValues: TFinancialConditionFormValues,
  localValues?: TFinancialConditionFormValues
): TFinancialConditionFormValues => {
  if (!localValues) {
    return loadedValues;
  }

  return {
    ...loadedValues,
    liabilitiesAnnual: mergeFinancialStatementRows(
      loadedValues.liabilitiesAnnual,
      localValues.liabilitiesAnnual
    ),
    liabilitiesQuarterly: mergeFinancialStatementRows(
      loadedValues.liabilitiesQuarterly,
      localValues.liabilitiesQuarterly
    ),
    financialInvestments: mergeTemplateFinancialStatementRows(
      loadedValues.financialInvestments,
      localValues.financialInvestments
    ),
    receivables: mergeTemplateFinancialStatementRows(
      loadedValues.receivables,
      localValues.receivables
    ),
    expensesAnnual: mergeFinancialStatementRows(
      loadedValues.expensesAnnual,
      localValues.expensesAnnual
    ),
    expensesQuarterly: mergeFinancialStatementRows(
      loadedValues.expensesQuarterly,
      localValues.expensesQuarterly
    ),
    expensesQuarterlySmall: mergeFinancialStatementRows(
      loadedValues.expensesQuarterlySmall,
      localValues.expensesQuarterlySmall
    ),
  };
};

export const createCourtCase = (): TCourtCase => ({
  otherParty: '',
  borrowerName: '',
  borrowerStatus: '',
  subject: '',
  claimAmount: '',
  claimDate: null,
  currentState: '',
});

export const getFinancialConditionDefaultValues = (
  financialStatementForm?: FinancialStatementForm
): TFinancialConditionFormValues => {
  const template = financialStatementForm
    ? FINANCIAL_STATEMENT_FORM_TEMPLATES[financialStatementForm]
    : undefined;

  return {
    borrowerType: null,
    financialStatementForm: null,
    borrowerName: '',
    borrowerCode: '',
    reportPeriod: REPORT_PERIOD_OPTIONS[0] ?? null,
    reportYear: String(new Date().getFullYear()),
    reportDate: null,
    hasCashFlowTransferRequirement: false,
    incomeBanks: [createBankIncomeRow()],
    hasAverageBalanceRequirement: false,
    averageBalanceBanks: [createBankAverageBalanceRow()],
    hasNoCreditOperations: false,
    creditOperations: [createCreditOperation()],
    providedFinancialAssistance: [createFinancialAssistance()],
    receivedFinancialAssistance: [createFinancialAssistance()],
    hasNoLoansAndBonds: false,
    receivedLoans: [createLoanOrBond()],
    bondLoans: [createLoanOrBond()],
    offBalanceLiabilities: [createOffBalanceLiability()],
    currencyExchanges: [createCurrencyExchange()],
    liabilitiesAnnual: template
      ? createRowsWithReportLines(template.liabilitiesAnnual, createLiability)
      : [createLiability()],
    liabilitiesQuarterly: template
      ? createRowsWithReportLines(template.liabilitiesQuarterly, createLiability)
      : [createLiability()],
    incomeBreakdowns: [createIncomeBreakdown()],
    financialInvestments: template
      ? createRowsWithTemplateCodes(template.financialInvestmentCodes, createFinancialInvestment)
      : [createFinancialInvestment()],
    receivables: template
      ? createRowsWithTemplateCodes(template.receivableCodes, createReceivable)
      : [createReceivable()],
    budgetDebts: [createBudgetDebt()],
    expensesAnnual: template
      ? createExpenseTemplateRows(template.expensesAnnual)
      : [createExpense()],
    expensesQuarterly: template
      ? createExpenseTemplateRows(template.expensesQuarterly)
      : [createExpense()],
    expensesQuarterlySmall: template
      ? createExpenseTemplateRows(template.expensesQuarterlySmall)
      : [createExpense()],
    courtCases: [createCourtCase()],
    additionalInfo: '',
  };
};

export const isMonthlyReportPeriod = (period: TAutocompleteOption | null): boolean =>
  period?.value.startsWith('month:') ?? false;

export const getReportPeriodMonthNames = (period: TAutocompleteOption | null): string[] => {
  if (isMonthlyReportPeriod(period)) {
    const monthValue = period?.value.replace('month:', '');

    return [MONTH_OPTIONS.find((month) => month.value === `month:${monthValue}`)?.label ?? ''];
  }

  const quarterValue = period?.value.replace('quarter:', '') as TQuarter | undefined;

  return QUARTER_MONTHS[quarterValue ?? '1'];
};

export const getCertificateBalanceMonthNames = (
  period: TAutocompleteOption | null,
  year: string
): [string, string, string] => {
  const normalizedYear = year.trim();

  const monthNames = getReportPeriodMonthNames(period).map((month) => {
    const normalizedMonth = `${month.charAt(0).toLocaleLowerCase('uk-UA')}${month.slice(1)}`;

    return normalizedYear ? `${normalizedMonth} ${normalizedYear}` : normalizedMonth;
  });

  return [monthNames[0] ?? '', monthNames[1] ?? '', monthNames[2] ?? ''];
};

export const formatCertificateDate = (value: string | Date | null): string | undefined => {
  const formattedDate = formatDateForDisplay(value);

  return formattedDate || undefined;
};

export const normalizeDecimalValue = (value: string): string => {
  const sanitizedValue = value
    .trim()
    .replace(/\s/g, '')
    .replace(/[^\d,.]/g, '');
  const [integerPart = '', ...fractionParts] = sanitizedValue.split(/[,.]/);
  const fractionPart = fractionParts.join('').slice(0, MAX_DECIMAL_FRACTION_DIGITS);

  if (!integerPart && !fractionPart) {
    return '';
  }

  return fractionPart ? `${integerPart || '0'},${fractionPart}` : integerPart;
};

export const normalizePercentageValue = (value: string): string =>
  normalizeDecimalValue(value.replace('%', ''));

export const normalizeBalanceLine = (value: string): string => {
  const lineNumber = value.replace(/\D/g, '').slice(0, 4);

  return lineNumber ? `р.${lineNumber}` : '';
};

export const validateBalanceLine = (value: string): true | string =>
  !value || /^р\.\d{4}$/.test(value) || 'Вкажіть рядок із 4 цифр';

export const formatThousandAmount = (value: number): string =>
  new Intl.NumberFormat('uk-UA', { maximumFractionDigits: MAX_DECIMAL_FRACTION_DIGITS }).format(
    value
  );

const getNumericValue = (value: string) => Number.parseFloat(value.replace(',', '.')) || 0;

const sortCurrencyTotals = <T extends { currency: TAutocompleteOption }>(totals: T[]): T[] =>
  totals.sort((first, second) => first.currency.label.localeCompare(second.currency.label, 'uk'));

export const getBankIncomeTotals = (incomeBanks: TBankIncomeRow[]): TBankIncomeCurrencyTotals[] => {
  const totals = new Map<string, TBankIncomeCurrencyTotals>();

  incomeBanks.forEach((incomeBank) => {
    const currency = incomeBank.currency;

    if (!currency) return;

    const currentTotals = totals.get(currency.value) ?? {
      currency,
      total: 0,
      salesRevenue: 0,
    };

    currentTotals.total += getNumericValue(incomeBank.total);
    currentTotals.salesRevenue += getNumericValue(incomeBank.salesRevenue);
    totals.set(currency.value, currentTotals);
  });

  return sortCurrencyTotals(Array.from(totals.values()));
};

export const getBankAverageBalanceTotals = (
  averageBalanceBanks: TBankAverageBalanceRow[]
): TBankAverageBalanceCurrencyTotals[] => {
  const totals = new Map<string, TBankAverageBalanceCurrencyTotals>();

  averageBalanceBanks.forEach((averageBalanceBank) => {
    const currency = averageBalanceBank.currency;

    if (!currency) return;

    const currentTotals = totals.get(currency.value) ?? {
      currency,
      firstMonthAmount: 0,
      secondMonthAmount: 0,
      thirdMonthAmount: 0,
    };

    currentTotals.firstMonthAmount += getNumericValue(averageBalanceBank.firstMonthAmount);
    currentTotals.secondMonthAmount += getNumericValue(averageBalanceBank.secondMonthAmount);
    currentTotals.thirdMonthAmount += getNumericValue(averageBalanceBank.thirdMonthAmount);
    totals.set(currency.value, currentTotals);
  });

  return sortCurrencyTotals(Array.from(totals.values()));
};

export const getCurrencyExchangeTotals = (
  currencyExchanges: TCurrencyExchange[]
): TCurrencyExchangeCurrencyTotals[] => {
  const totals = new Map<string, TCurrencyExchangeCurrencyTotals>();

  const addAmount = (
    currency: TAutocompleteOption | null,
    amount: string,
    field: 'soldAmount' | 'boughtAmount'
  ) => {
    if (!currency) return;

    const currentTotals = totals.get(currency.value) ?? { currency };

    currentTotals[field] = (currentTotals[field] ?? 0) + getNumericValue(amount);
    totals.set(currency.value, currentTotals);
  };

  currencyExchanges.forEach((currencyExchange) => {
    addAmount(currencyExchange.soldCurrency, currencyExchange.soldAmount, 'soldAmount');
    addAmount(currencyExchange.boughtCurrency, currencyExchange.boughtAmount, 'boughtAmount');
  });

  return sortCurrencyTotals(Array.from(totals.values()));
};

export const getCreditOperationTotals = (
  creditOperations: TCreditOperation[]
): TCreditOperationCurrencyTotals[] => {
  const totals = new Map<string, TCreditOperationCurrencyTotals>();

  creditOperations.forEach((operation) => {
    const currency = operation.currency;

    if (!currency) return;

    const currentTotals = totals.get(currency.value) ?? {
      currency,
      limit: 0,
      outstandingDebt: 0,
      overdueDebt: 0,
    };

    currentTotals.limit += getNumericValue(operation.limit);
    currentTotals.outstandingDebt += getNumericValue(operation.outstandingDebt);
    currentTotals.overdueDebt += getNumericValue(operation.overdueDebt);
    totals.set(currency.value, currentTotals);
  });

  return sortCurrencyTotals(Array.from(totals.values()));
};

export const getFinancialAssistanceTotals = (
  financialAssistance: TFinancialAssistance[]
): TFinancialAssistanceCurrencyTotals[] => {
  const totals = new Map<string, TFinancialAssistanceCurrencyTotals>();

  financialAssistance.forEach((assistance) => {
    const currency = assistance.currency;

    if (!currency) return;

    const currentTotals = totals.get(currency.value) ?? {
      currency,
      outstandingDebt: 0,
      overdueDebt: 0,
    };

    currentTotals.outstandingDebt += getNumericValue(assistance.outstandingDebt);
    currentTotals.overdueDebt += getNumericValue(assistance.overdueDebt);
    totals.set(currency.value, currentTotals);
  });

  return sortCurrencyTotals(Array.from(totals.values()));
};
